import React, { useEffect, useState, useCallback } from "react";
import { Globe2, Grid3x3 } from "lucide-react";

/**
 * useDimension — persists the user's preferred view mode ("3d" | "2d") in
 * localStorage under a single Fleet-wide key. Falls back to "3d" on first
 * visit (Director directive 2026.02 · the 3D sphere is the master visual).
 *
 * Returns [dimension, toggle, setDimension].
 */
const STORAGE_KEY = "eigoo_dimension";

export function useDimension(defaultMode = "3d") {
    const [dimension, setDimensionState] = useState(() => {
        if (typeof window === "undefined") return defaultMode;
        try {
            const stored = window.localStorage.getItem(STORAGE_KEY);
            if (stored === "2d" || stored === "3d") return stored;
        } catch { /* private mode — fall through */ }
        return defaultMode;
    });

    const setDimension = useCallback((mode) => {
        if (mode !== "2d" && mode !== "3d") return;
        setDimensionState(mode);
        try { window.localStorage.setItem(STORAGE_KEY, mode); } catch { /* noop */ }
        // Announce to other tabs / mounted components on the same page.
        try { window.dispatchEvent(new CustomEvent("eigoo:dimension", { detail: mode })); } catch { /* noop */ }
    }, []);

    const toggle = useCallback(() => {
        setDimension(dimension === "3d" ? "2d" : "3d");
    }, [dimension, setDimension]);

    // Listen for dimension changes from sibling toggles on the same page.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === STORAGE_KEY && (e.newValue === "2d" || e.newValue === "3d")) {
                setDimensionState(e.newValue);
            }
        };
        const onCustom = (e) => {
            if (e?.detail === "2d" || e?.detail === "3d") setDimensionState(e.detail);
        };
        window.addEventListener("storage", onStorage);
        window.addEventListener("eigoo:dimension", onCustom);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("eigoo:dimension", onCustom);
        };
    }, []);

    return [dimension, toggle, setDimension];
}

/**
 * <DimensionToggle /> — pill-style segmented control.
 *
 * Drop near any 3D/2D-aware visual:
 *   const [dimension, toggle] = useDimension();
 *   <DimensionToggle dimension={dimension} onChange={setDimension} />
 *   {dimension === "3d" ? <TerritorySphere ... /> : <USMap ... />}
 */
export default function DimensionToggle({ dimension, onChange, size = "md", className = "" }) {
    const dims = ["3d", "2d"];
    const padding = size === "sm" ? "px-2 py-1 text-[10px]" : "px-3 py-1.5 text-xs";

    return (
        <div
            data-testid="dimension-toggle"
            className={`inline-flex rounded-full border border-[hsl(40_40%_30%/0.4)] p-0.5 bg-[hsl(151_55%_10%)] ${className}`}
            role="tablist"
            aria-label="View dimension"
        >
            {dims.map((d) => {
                const active = dimension === d;
                const Icon = d === "3d" ? Globe2 : Grid3x3;
                return (
                    <button
                        key={d}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        data-testid={`dimension-toggle-${d}`}
                        onClick={() => onChange?.(d)}
                        className={`inline-flex items-center gap-1.5 rounded-full uppercase tracking-[0.2em] font-medium transition-all ${padding} ${
                            active
                                ? "bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] shadow-[0_2px_8px_hsl(40_70%_45%/0.4)]"
                                : "text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        <Icon size={size === "sm" ? 11 : 13} />
                        {d === "3d" ? "3D Sphere" : "2D Grid"}
                    </button>
                );
            })}
        </div>
    );
}
